/** Accessible radio group. Wraps React Aria RadioGroup and Radio. */
import {
  RadioGroup as AriaRadioGroup,
  Radio as AriaRadio,
  Label,
  type RadioGroupProps as AriaRadioGroupProps,
  type RadioProps as AriaRadioProps,
} from 'react-aria-components';
import type { ReactNode } from 'react';
import { cn } from '@/foundation/utils/cn';

interface RadioGroupProps extends Omit<AriaRadioGroupProps, 'children'> {
  label?: string;
  children: ReactNode;
  className?: string;
  description?: string;
  orientation?: 'horizontal' | 'vertical';
}

function RadioGroup({
  label,
  children,
  className,
  description,
  orientation = 'vertical',
  ...props
}: RadioGroupProps) {
  return (
    <AriaRadioGroup {...props} className={cn('flex flex-col gap-2', className)}>
      {label && (
        <Label className="text-[length:var(--f-text-sm)] font-medium text-[color:var(--f-fg)]">
          {label}
        </Label>
      )}
      {description && (
        <p className="text-[length:var(--f-text-xs)] text-[color:var(--f-fg-muted)] -mt-1">
          {description}
        </p>
      )}
      <div
        className={cn('flex gap-3', orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap')}
      >
        {children}
      </div>
    </AriaRadioGroup>
  );
}

interface RadioProps extends Omit<AriaRadioProps, 'children'> {
  children: ReactNode;
  description?: string;
  className?: string;
}

function Radio({ children, description, className, ...props }: RadioProps) {
  return (
    <AriaRadio
      {...props}
      className={cn('group flex items-start gap-2.5 cursor-pointer', className)}
    >
      {(renderProps) => (
        <>
          <div
            className={cn(
              'mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors',
              renderProps.isSelected
                ? 'border-[color:var(--f-accent)] bg-[color:var(--f-accent)]'
                : 'border-[color:var(--f-border-strong)]',
              renderProps.isFocusVisible && 'ring-2 ring-offset-1 ring-[color:var(--f-accent)]',
              renderProps.isDisabled && 'opacity-40 cursor-not-allowed'
            )}
          >
            {renderProps.isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
          </div>
          <div className="flex flex-col">
            <span className="text-[length:var(--f-text-sm)] text-[color:var(--f-fg)]">
              {children}
            </span>
            {description && (
              <span className="text-[length:var(--f-text-xs)] text-[color:var(--f-fg-muted)]">
                {description}
              </span>
            )}
          </div>
        </>
      )}
    </AriaRadio>
  );
}

export { RadioGroup, Radio };
export type { RadioGroupProps, RadioProps };
